import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, FormGroup, Input } from "reactstrap";
import SummaryCard from "../../../components/SummaryCard/SummaryCard";
import { useNavigate } from "react-router-dom";
import { post } from "helpers/api_helper";
import * as api from "../../../constants/api_constants";
import { showWarningToast } from "helpers/toastMessages";

const ScreeningRound = ({
  setEvaluationStatus,
  setActiveRound,
  evaluationStatus,
  jobId,
  candidatedetails,
  interviewStageId,
}) => {
  const navigate = useNavigate();

  // Helper function to find the screeningRoundDto by interviewStageId
  const getRelevantScreeningRound = (interviews, stageId) => {
    if (!interviews || interviews.length === 0) {
      return null;
    }

    // First, try to find the screeningRoundDto matching the interviewStageId
    const matchingInterview = interviews.find(
      (interview) =>
        interview.interviewStageId === stageId && interview.screeningRoundDto
    );

    if (matchingInterview) {
      return matchingInterview.screeningRoundDto;
    }

    // Fallback: Select the first screeningRoundDto with actual data
    return (
      interviews
        .filter((interview) => interview.screeningRoundDto)
        .find((interview) => {
          const { screeningRoundDto } = interview;
          return (
            screeningRoundDto.technicalSkills !== "Not Evaluated" ||
            screeningRoundDto.relevantExperience !== "Not Evaluated" ||
            screeningRoundDto.educationalQualification !== "Not Evaluated" ||
            screeningRoundDto.communicationSkills !== "Not Evaluated" ||
            screeningRoundDto.resumeQuality !== "Not Evaluated" ||
            screeningRoundDto.notes !== "No notes provided"
          );
        })?.screeningRoundDto ||
      interviews[0]?.screeningRoundDto ||
      null
    );
  };

  // Determine the relevant screeningRoundDto using interviewStageId
  const relevantScreeningRound = getRelevantScreeningRound(
    candidatedetails?.job?.[0]?.interviews,
    interviewStageId
  );

  // Determine if the component should be editable
  const isEditable =
    !interviewStageId &&
    (!relevantScreeningRound || relevantScreeningRound.evaluationOutcome === null);

  // Initialize resumeReview state
  const [resumeReview, setResumeReview] = useState({
    technicalSkills: relevantScreeningRound?.technicalSkills || "",
    relevantExperience: relevantScreeningRound?.relevantExperience || "",
    educationalQualification:
      relevantScreeningRound?.educationalQualification || "",
    communicationSkills: relevantScreeningRound?.communicationSkills || "",
    resumeQuality: relevantScreeningRound?.resumeQuality || "",
  });

  const [note, setNote] = useState(relevantScreeningRound?.notes || "");
  const [localEvaluationStatus, setLocalEvaluationStatus] = useState("in progress");

  useEffect(() => {
    setResumeReview({
      technicalSkills: relevantScreeningRound?.technicalSkills || "",
      relevantExperience: relevantScreeningRound?.relevantExperience || "",
      educationalQualification:
        relevantScreeningRound?.educationalQualification || "",
      communicationSkills: relevantScreeningRound?.communicationSkills || "",
      resumeQuality: relevantScreeningRound?.resumeQuality || "",
    });
    setNote(relevantScreeningRound?.notes || "");

    // Normalize evaluationOutcome for comparison
    const outcome = relevantScreeningRound?.evaluationOutcome
      ?.toLowerCase()
      ?.replace(/\s+/g, "_"); // Replace spaces with underscores
    if (outcome === "move_to_next_round" || outcome === "move_to_next_round") {
      setLocalEvaluationStatus("advance");
    } else if (outcome === "put_on_hold") {
      setLocalEvaluationStatus("hold");
    } else if (outcome === "reject") {
      setLocalEvaluationStatus("rejected");
    } else {
      setLocalEvaluationStatus("in progress");
    }
  }, [relevantScreeningRound]);

  // Handle changes for resume review radio buttons
  const handleReviewChange = (category, value) => {
    if (isEditable) {
      setResumeReview((prev) => ({
        ...prev,
        [category]: value,
      }));
    }
  };

  // Handle note change
  const handleNoteChange = (e) => {
    if (isEditable) {
      setNote(e.target.value);
    }
  };

  // Handle evaluation change
  const handleEvaluationChange = async (status) => {
    if (!isEditable) return;

    // Validation for resumeQuality and notes when advancing
    if (status === "advance") {
      if (!resumeReview.resumeQuality || !note.trim()) {
        showWarningToast(
          "Please provide both Resume Quality and Notes before moving to the next round."
        );
        return;
      }
    }

    setLocalEvaluationStatus(status);
    setEvaluationStatus(status);

    const evaluationOutcomeMap = {
      advance: "Move to next round",
      hold: "Put on hold",
      rejected: "Reject",
    };

    const evaluationOutcome = evaluationOutcomeMap[status] || status.toUpperCase();

    // Construct payload for API
    const payload = {
      delFlag: 1,
      jobId: jobId,
      candidateId: candidatedetails?.id,
      screeningRoundDto: {
        technicalSkills: resumeReview.technicalSkills || "Not Evaluated",
        relevantExperience: resumeReview.relevantExperience || "Not Evaluated",
        educationalQualification:
          resumeReview.educationalQualification || "Not Evaluated",
        communicationSkills:
          resumeReview.communicationSkills || "Not Evaluated",
        resumeQuality: resumeReview.resumeQuality || "Not Evaluated",
        notes: note.trim() || "No notes provided",
        evaluationOutcome: evaluationOutcome,
        roundType: "Screening Round",
      },
    };

    try {
      await post(api.CREATE_INTERVIEWS, payload);
      if (status === "advance") {
        setActiveRound("technical");
      }
      if (status === "hold" || status === "rejected") {
        navigate(`/job-details/${jobId}`);
      }
    } catch (error) {
      console.error("Failed to submit screening round data:", error);
    }
  };

  return (
    <div>
      <Container fluid className="">
        <Row className="mt-5">
          <Col>
            {/* Resume Review Section */}
            <SummaryCard title="Resume Review">
              <Row className="text-black">
                <Row>
                  <Col md={12}>
                    <div
                      className="px-4 rounded"
                      style={{ backgroundColor: "#F5F6F8" }}
                    >
                      <p className="mb-4">
                        {isEditable
                          ? "Rate how well the candidate's resume aligns with the job requirements:"
                          : "Candidate's resume evaluation (view-only):"}
                      </p>

                      {/* Technical Skills */}
                      <Row className="mb-3">
                        <Col md={3}>
                          <h6 className="mb-2">Technical Skills</h6>
                        </Col>
                        <Col>
                          <div className="d-flex gap-4 align-items-center">
                            {["Excellent", "Good", "Average", "Poor"].map(
                              (option) => (
                                <div
                                  key={option}
                                  className="form-check d-flex align-items-center"
                                >
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="technicalSkills"
                                    id={`technicalSkills-${option}`}
                                    value={option}
                                    checked={
                                      resumeReview.technicalSkills === option
                                    }
                                    onChange={() =>
                                      handleReviewChange(
                                        "technicalSkills",
                                        option
                                      )
                                    }
                                    disabled={!isEditable}
                                    style={{
                                      width: "25px",
                                      height: "25px",
                                      marginRight: "8px",
                                    }}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`technicalSkills-${option}`}
                                    style={{
                                      fontSize: "15px",
                                      fontWeight: "400",
                                      marginTop: "8px",
                                      color:
                                        !isEditable &&
                                        resumeReview.technicalSkills === option
                                          ? "#000"
                                          : "#6c757d",
                                    }}
                                  >
                                    {option}
                                  </label>
                                </div>
                              )
                            )}
                          </div>
                        </Col>
                      </Row>

                      {/* Relevant Experience */}
                      <Row className="mb-3">
                        <Col md={3}>
                          <h6 className="mb-2">Relevant Experience</h6>
                        </Col>
                        <Col>
                          <div className="d-flex gap-4 align-items-center">
                            {[
                              "Strongly Relevant",
                              "Somewhat Relevant",
                              "Not Relevant",
                            ].map((option) => (
                              <div
                                key={option}
                                className="form-check d-flex align-items-center"
                              >
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="relevantExperience"
                                  id={`relevantExperience-${option}`}
                                  value={option}
                                  checked={
                                    resumeReview.relevantExperience === option
                                  }
                                  onChange={() =>
                                    handleReviewChange(
                                      "relevantExperience",
                                      option
                                    )
                                  }
                                  disabled={!isEditable}
                                  style={{
                                    width: "25px",
                                    height: "25px",
                                    marginRight: "8px",
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`relevantExperience-${option}`}
                                  style={{
                                    fontSize: "15px",
                                    fontWeight: "400",
                                    marginTop: "8px",
                                    color:
                                      !isEditable &&
                                      resumeReview.relevantExperience === option
                                        ? "#000"
                                        : "#6c757d",
                                  }}
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>

                      {/* Educational Qualification */}
                      <Row className="mb-3">
                        <Col md={3}>
                          <h6 className="mb-2">Educational Qualification</h6>
                        </Col>
                        <Col>
                          <div className="d-flex gap-4 align-items-center">
                            {[
                              "Meets Required Level",
                              "Below Required Level",
                              "Exceeds Requirements",
                            ].map((option) => (
                              <div
                                key={option}
                                className="form-check d-flex align-items-center"
                              >
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="education"
                                  id={`education-${option}`}
                                  value={option}
                                  checked={
                                    resumeReview.educationalQualification ===
                                    option
                                  }
                                  onChange={() =>
                                    handleReviewChange(
                                      "educationalQualification",
                                      option
                                    )
                                  }
                                  disabled={!isEditable}
                                  style={{
                                    width: "25px",
                                    height: "25px",
                                    marginRight: "8px",
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`education-${option}`}
                                  style={{
                                    fontSize: "15px",
                                    fontWeight: "400",
                                    marginTop: "8px",
                                    color:
                                      !isEditable &&
                                      resumeReview.educationalQualification ===
                                        option
                                        ? "#000"
                                        : "#6c757d",
                                  }}
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>

                      {/* Communication Skills */}
                      <Row className="mb-3">
                        <Col md={3}>
                          <h6 className="mb-2">Communication Skills</h6>
                        </Col>
                        <Col>
                          <div className="d-flex gap-4 align-items-center">
                            {[
                              "Well-articulated",
                              "Average",
                              "Unclear/Needs Verification",
                            ].map((option) => (
                              <div
                                key={option}
                                className="form-check d-flex align-items-center"
                              >
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="communication"
                                  id={`communication-${option}`}
                                  value={option}
                                  checked={
                                    resumeReview.communicationSkills === option
                                  }
                                  onChange={() =>
                                    handleReviewChange(
                                      "communicationSkills",
                                      option
                                    )
                                  }
                                  disabled={!isEditable}
                                  style={{
                                    width: "25px",
                                    height: "25px",
                                    marginRight: "8px",
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`communication-${option}`}
                                  style={{
                                    fontSize: "15px",
                                    fontWeight: "400",
                                    marginTop: "8px",
                                    color:
                                      !isEditable &&
                                      resumeReview.communicationSkills ===
                                        option
                                        ? "#000"
                                        : "#6c757d",
                                  }}
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>

                      {/* Resume Quality */}
                      <Row className="mb-3">
                        <Col md={3}>
                          <h6 className="mb-2">Resume Quality</h6>
                        </Col>
                        <Col>
                          <div className="d-flex gap-4 align-items-center">
                            {[
                              "Well-structured & detailed",
                              "Moderate",
                              "Lacks clarity or formatting",
                            ].map((option) => (
                              <div
                                key={option}
                                className="form-check d-flex align-items-center"
                              >
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="resumeQuality"
                                  id={`resumeQuality-${option}`}
                                  value={option}
                                  checked={
                                    resumeReview.resumeQuality === option
                                  }
                                  onChange={() =>
                                    handleReviewChange("resumeQuality", option)
                                  }
                                  disabled={!isEditable}
                                  style={{
                                    width: "25px",
                                    height: "25px",
                                    marginRight: "8px",
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`resumeQuality-${option}`}
                                  style={{
                                    fontSize: "15px",
                                    fontWeight: "400",
                                    marginTop: "8px",
                                    color:
                                      !isEditable &&
                                      resumeReview.resumeQuality === option
                                        ? "#000"
                                        : "#6c757d",
                                  }}
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Row>
            </SummaryCard>

            {/* Notes Section */}
            <SummaryCard title="Notes">
              <FormGroup className="mb-3">
                <Row className="align-items-center">
                  <Col>
                    <Input
                      name="note"
                      type="textarea"
                      value={note}
                      onChange={handleNoteChange}
                      disabled={!isEditable}
                      style={{
                        border: "2px solid #E3E8EE",
                        height: "100px",
                        borderRadius: "12px",
                        backgroundColor: !isEditable ? "#f8f9fa" : "#fff",
                      }}
                      placeholder={
                        isEditable ? "Add Note ..." : "No notes provided"
                      }
                      readOnly={!isEditable}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </SummaryCard>

            {/* Evaluation Outcome */}
            <SummaryCard title="Evaluation Outcome">
              <Row>
                <Col>
                  <Button
                    onClick={() => handleEvaluationChange("rejected")}
                    disabled={!isEditable}
                    style={{
                      paddingRight: "40px",
                      paddingLeft: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      marginRight: "10px",
                      backgroundColor: "#F93131",
                      color: "white",
                      border:
                        localEvaluationStatus === "rejected"
                          ? "2px solid #000"
                          : "none",
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleEvaluationChange("hold")}
                    disabled={!isEditable}
                    style={{
                      paddingRight: "20px",
                      paddingLeft: "20px",
                      height: "40px",
                      borderRadius: "12px",
                      marginRight: "10px",
                      backgroundColor: "#1E9EFB",
                      color: "white",
                      border:
                        localEvaluationStatus === "hold"
                          ? "2px solid #000"
                          : "none",
                    }}
                  >
                    Put On Hold
                  </Button>
                  <Button
                    onClick={() => handleEvaluationChange("advance")}
                    disabled={
                      !isEditable ||
                      localEvaluationStatus === "rejected" ||
                      localEvaluationStatus === "hold"
                    }
                    style={{
                      paddingRight: "10px",
                      paddingLeft: "10px",
                      height: "40px",
                      borderRadius: "12px",
                      marginRight: "10px",
                      backgroundColor: "#006438",
                      color: "white",
                      border:
                        localEvaluationStatus === "advance"
                          ? "2px solid #000"
                          : "none",
                    }}
                  >
                    Move to Next Round
                  </Button>
                </Col>
              </Row>
            </SummaryCard>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ScreeningRound;